import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ActivityTimeline from './components/ActivityTimeline';
import ActivityFilters from './components/ActivityFilters';
import QuickAddActivity from './components/QuickAddActivity';
import BulkActions from './components/BulkActions';
import ActivityStats from './components/ActivityStats';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const Activities = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ search: '', type: 'all', status: 'all', owner: 'all', priority: 'all', dateFrom: '', dateTo: '' });
  const { profile } = useAuth();

  const fetchActivities = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    setError('');
    try {
      let query = supabase?.from('activities')?.select('*, contacts(first_name, last_name), deals(name), owner:profiles(full_name)')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order('due_date', { ascending: true });

      if (filters?.type !== 'all') query = query?.eq('type', filters?.type);
      if (filters?.status !== 'all') query = query?.eq('status', filters?.status);
      if (filters?.priority !== 'all') query = query?.eq('priority', filters?.priority);
      if (filters?.dateFrom) query = query?.gte('due_date', filters?.dateFrom);
      if (filters?.dateTo) query = query?.lte('due_date', filters?.dateTo + 'T23:59:59');

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      const mapped = (data || [])?.map(a => ({
        id: a?.id,
        type: a?.type,
        title: a?.subject,
        description: a?.description || '',
        dueDate: a?.due_date ? new Date(a.due_date) : new Date(),
        priority: a?.priority || 'medium',
        owner: a?.owner?.full_name || 'Unassigned',
        contact: a?.contacts ? `${a?.contacts?.first_name} ${a?.contacts?.last_name}` : null,
        account: a?.deals?.name || null,
        completed: a?.status === 'completed',
        notes: a?.description || '',
        status: a?.status
      }));

      if (filters?.search) {
        const term = filters?.search?.toLowerCase();
        return setActivities(mapped?.filter(a =>
          a?.title?.toLowerCase()?.includes(term) ||
          a?.description?.toLowerCase()?.includes(term) ||
          a?.contact?.toLowerCase()?.includes(term)
        ));
      }

      setActivities(mapped);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id, filters]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  // Keep a ref to always have the latest fetchActivities for realtime callback
  const fetchActivitiesRef = useRef(fetchActivities);
  useEffect(() => { fetchActivitiesRef.current = fetchActivities; }, [fetchActivities]);

  // Realtime — use org_id in channel name to avoid conflicts, ref to avoid stale closure
  useEffect(() => {
    if (!profile?.org_id) return;
    const channelName = `activities_realtime_${profile?.org_id}`;
    const channel = supabase
      ?.channel(channelName)
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'activities',
        filter: `org_id=eq.${profile?.org_id}`
      }, () => fetchActivitiesRef?.current())
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [profile?.org_id]);

  const handleMenuToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
  const handleClearFilters = () => setFilters({ search: '', type: 'all', status: 'all', owner: 'all', priority: 'all', dateFrom: '', dateTo: '' });

  const handleAddActivity = async (newActivity) => {
    try {
      await supabase?.from('activities')?.insert({
        org_id: profile?.org_id,
        owner_id: profile?.id,
        type: newActivity?.type || 'task',
        subject: newActivity?.title,
        description: newActivity?.notes || '',
        due_date: newActivity?.dueDate || new Date()?.toISOString(),
        priority: (newActivity?.priority || 'medium')?.toLowerCase(),
        status: 'pending'
      });
      fetchActivities();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleCompleteActivity = async (activityId) => {
    try {
      await supabase?.from('activities')?.update({ status: 'completed' })?.eq('id', activityId);
      fetchActivities();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      await supabase?.from('activities')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', activityId);
      toast?.success('Moved to Trash');
      fetchActivities();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleSelectActivity = (activityId, checked) => {
    if (checked) setSelectedActivities(prev => [...prev, activityId]);
    else setSelectedActivities(prev => prev?.filter(id => id !== activityId));
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedActivities(activities?.map(a => a?.id));
    else setSelectedActivities([]);
  };

  const stats = {
    total: activities?.length,
    completed: activities?.filter(a => a?.completed)?.length,
    pending: activities?.filter(a => !a?.completed)?.length,
    overdue: activities?.filter(a => !a?.completed && new Date(a.dueDate) < new Date())?.length
  };

  return (
    <>
      <Helmet>
        <title>Activities - Wilcart CRM</title>
        <meta name="description" content="Track and manage your sales activities in Wilcart CRM." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Activities</h1>
                <p className="text-muted-foreground mt-1">Track calls, meetings, tasks, and emails</p>
              </div>
              <Button onClick={() => setIsQuickAddOpen(true)}>
                <Icon name="Plus" size={16} className="mr-2" />
                Add Activity
              </Button>
            </div>

            <ActivityStats stats={stats} activities={activities} />

            <ActivityFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              totalCount={activities?.length}
              filteredCount={activities?.length}
            />

            {selectedActivities?.length > 0 && (
              <BulkActions
                selectedCount={selectedActivities?.length}
                onMarkComplete={() => selectedActivities?.forEach(id => handleCompleteActivity(id))}
                onDelete={() => selectedActivities?.forEach(id => handleDeleteActivity(id))}
                onReassign={() => {}}
                onClearSelection={() => setSelectedActivities([])}
              />
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>
            )}

            {loading ? (
              <div className="space-y-3">
                {[1,2,3,4]?.map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />)}
              </div>
            ) : (
              <ActivityTimeline
                activities={activities}
                selectedActivities={selectedActivities}
                onSelectActivity={handleSelectActivity}
                onSelectAll={handleSelectAll}
                onComplete={handleCompleteActivity}
                onEdit={() => {}}
                onReschedule={() => {}}
                onDelete={handleDeleteActivity}
              />
            )}
          </div>
        </main>

        <QuickAddActivity
          isOpen={isQuickAddOpen}
          onClose={() => setIsQuickAddOpen(false)}
          onAdd={handleAddActivity}
        />
      </div>
    </>
  );
};

export default Activities;